import { Box, createStyles, makeStyles } from '@material-ui/core'
import { Label } from '@noco/http-client/lib/noco'
import React, { useCallback, useEffect, useState } from 'react'
import { Colors, Input, Toaster, Typography } from 'src/components/atoms'
import { PopupTemplate } from '..'
import { useCreateLabel, useUpdateLabel } from 'src/fixtures/modules/labels/hooks'

const useStyles = makeStyles(() =>
  createStyles({
    colorInput: {
      width: '48px',
      height: '48px',
      padding: '0',
      border: `1px solid ${Colors.background.silver}`,
      borderRadius: '4px',
      cursor: 'pointer',
      '&::-webkit-color-swatch-wrapper': {
        padding: '0'
      },
      '&::-webkit-color-swatch': {
        border: 'none',
        borderRadius: '4px'
      }
    },
    colorPreview: {
      fontFamily: 'monospace',
      fontSize: '14px',
      color: Colors.base.middleGray
    },
    required: {
      color: Colors.accent.keyRed.default,
      marginLeft: '4px'
    },
    helperText: {
      fontSize: '12px',
      color: Colors.base.middleGray,
      marginTop: '4px'
    }
  })
)

export interface ModalLabelEditProps {
  open: boolean
  label?: Label
  onClose: () => void
}

export const ModalLabelEdit = ({ open, label, onClose }: ModalLabelEditProps) => {
  const classes = useStyles()
  const [name, setName] = useState('')
  const [color, setColor] = useState('#CFEDC6')
  const { handleCreateLabel, isLoading: isCreating } = useCreateLabel()
  const { handleUpdateLabel, isLoading: isUpdating } = useUpdateLabel()
  const isLoading = isCreating || isUpdating

  const isEdit = !!label

  // 処理中はモーダルを閉じないようにする
  const handleClose = useCallback(() => {
    if (!isLoading) {
      onClose()
    }
  }, [isLoading, onClose])

  useEffect(() => {
    if (open) {
      if (label) {
        setName(label.name ?? '')
        setColor(label.color ?? '#CFEDC6')
      } else {
        setName('')
        setColor('#CFEDC6')
      }
    }
  }, [open, label])

  const handleNameChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value)
  }, [])

  const handleColorChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setColor(e.target.value)
  }, [])

  const handleConfirm = useCallback(async () => {
    if (!name.trim()) {
      Toaster.error('ラベル名を入力してください')
      return
    }

    let result
    if (isEdit && label?.id) {
      result = await handleUpdateLabel(label.id, { name: name.trim(), color })
    } else {
      result = await handleCreateLabel({ name: name.trim(), color })
    }
    // 成功時のみモーダルを閉じる
    if (result) {
      onClose()
    }
  }, [name, color, isEdit, label, handleCreateLabel, handleUpdateLabel, onClose])

  return (
    <PopupTemplate
      open={open}
      title={isEdit ? 'ラベルを編集' : 'ラベルを追加'}
      hasClose
      hasCancel
      cancelButtonLabel="キャンセル"
      onClose={handleClose}
      hasConfirm
      onConfirm={handleConfirm}
      confirmButtonLabel="保存"
      width="480px"
    >
      <Box px="24px" py="24px">
        <Box mb="20px">
          <Typography fontSize="s" fontWeight="bold">
            ラベル名<span className={classes.required}>*</span>
          </Typography>
          <Box height="8px" />
          <Input
            name="labelName"
            fullWidth
            value={name}
            onChange={handleNameChange}
            placeholder="ラベル名を入力"
            maxLength={10}
          />
          <Box className={classes.helperText}>最大10文字</Box>
        </Box>
        <Box>
          <Typography fontSize="s" fontWeight="bold">
            カラー<span className={classes.required}>*</span>
          </Typography>
          <Box height="8px" />
          <Box display="flex" alignItems="center" gridGap="12px">
            <input type="color" value={color} onChange={handleColorChange} className={classes.colorInput} />
            <span className={classes.colorPreview}>{color.toUpperCase()}</span>
          </Box>
        </Box>
      </Box>
    </PopupTemplate>
  )
}
